import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import ProductCard from '../components/ProductCard';
import { products } from '../data/products';

const Home = () => {
  const featuredProducts = products.slice(0, 4);

  return (
    <div className="flex flex-col min-h-screen">
      {/* Hero Section */}
      <section className="relative h-[80vh] min-h-[600px] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img 
            src="https://images.unsplash.com/photo-1463320726281-696a485928c7?ixlib=rb-4.0.3&auto=format&fit=crop&w=2000&q=80" 
            alt="Lush indoor plants" 
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-botanical-dark/40"></div>
        </div>
        
        <div className="relative z-10 text-center px-4 max-w-3xl mx-auto">
          <motion.h1 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-5xl md:text-7xl font-serif text-cream mb-6 leading-tight"
          >
            Bring the outdoors <span className="italic text-clay-light">in.</span>
          </motion.h1>
          <motion.p 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-lg md:text-xl text-cream/90 mb-10 font-light"
          >
            Curated houseplants, premium accessories, and the guidance you need to cultivate your personal sanctuary.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
          >
            <Link to="/shop" className="inline-block bg-clay hover:bg-clay-dark text-white px-8 py-4 rounded-full font-medium transition-colors text-lg tracking-wide shadow-lg">
              Shop the Collection
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Shop by Light Level */}
      <section className="py-20 bg-cream">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-serif text-botanical-dark mb-4">Find Your Perfect Match</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">Shop by the light available in your space to ensure your new plant thrives from day one.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { title: 'Low Light', desc: 'For north-facing windows or darker corners.', img: 'https://images.unsplash.com/photo-1593482892290-f54927ae2b7e?auto=format&fit=crop&w=600&q=80', icon: FiIcons.FiMoon },
              { title: 'Bright Indirect', desc: 'For east/west windows. Gentle, filtered sun.', img: 'https://images.unsplash.com/photo-1614594975525-e45190c55d40?auto=format&fit=crop&w=600&q=80', icon: FiIcons.FiSun },
              { title: 'Direct Sun', desc: 'For south-facing windows. Loves to bask.', img: 'https://images.unsplash.com/photo-1611211232932-da3113c5b960?auto=format&fit=crop&w=600&q=80', icon: FiIcons.FiSunrise },
            ].map((category, idx) => (
              <Link to="/shop" key={idx} className="group relative h-96 rounded-2xl overflow-hidden cursor-pointer">
                <img src={category.img} alt={category.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                <div className="absolute inset-0 bg-gradient-to-t from-botanical-dark/90 via-botanical-dark/20 to-transparent"></div>
                <div className="absolute bottom-0 left-0 p-8 w-full">
                  <div className="flex items-center gap-3 mb-2 text-clay-light">
                    <SafeIcon icon={category.icon} className="text-2xl" />
                    <h3 className="text-2xl font-serif text-cream">{category.title}</h3>
                  </div>
                  <p className="text-cream/80 text-sm transform translate-y-4 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-all duration-300">{category.desc}</p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Products */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-end mb-12">
            <div>
              <h2 className="text-3xl md:text-4xl font-serif text-botanical-dark mb-4">New Arrivals</h2>
              <p className="text-gray-600">Fresh from the greenhouse to your living room.</p>
            </div>
            <Link to="/shop" className="hidden md:flex items-center gap-2 text-clay font-medium hover:text-clay-dark transition-colors">
              View All <SafeIcon icon={FiIcons.FiArrowRight} />
            </Link>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {featuredProducts.map(product => (
              <ProductCard key={product.id} product={product} />
            ))}
          </div>
          <div className="mt-8 text-center md:hidden">
             <Link to="/shop" className="inline-flex items-center gap-2 text-clay font-medium hover:text-clay-dark transition-colors">
              View All <SafeIcon icon={FiIcons.FiArrowRight} />
            </Link>
          </div>
        </div>
      </section>

      {/* Guarantee Section */}
      <section className="py-24 bg-botanical text-cream relative overflow-hidden">
        <div className="absolute top-0 right-0 -mt-20 -mr-20 text-botanical-light/20">
           <SafeIcon icon={FiIcons.FiFeather} className="w-96 h-96" />
        </div>
        <div className="max-w-4xl mx-auto px-4 text-center relative z-10">
          <SafeIcon icon={FiIcons.FiPackage} className="text-5xl text-clay mx-auto mb-6" />
          <h2 className="text-3xl md:text-5xl font-serif mb-6">The RootRoom Guarantee</h2>
          <p className="text-lg md:text-xl text-cream/90 leading-relaxed font-light">
            We take pride in our careful packaging and healthy stock. We guarantee your plant will arrive at your door happy and in good condition. If your new green friend suffers significant damage in transit, simply contact us within 14 days of delivery, and we will gladly arrange a replacement.
          </p>
        </div>
      </section>
    </div>
  );
};

export default Home;